import React, { useContext, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import Header from '../../components/Header';
import InputForm from '../../components/InputForm';
import LoadingSpinner from '../../components/LoadingSpinner';
import SuccessSpinner from '../../components/SuccessSpinner';

import { InternalServiceException, SessionExpiredException } from '../../utils/errors';

import './index.css';

const IndexPage: React.FC = () => {
    const [loadingRequest, setLoadingRequest] = useState(false);
    const [successfulRequest, setSuccessfulRequest] = useState(false);
    const [errorRequest, setErrorRequest] = useState<string | null>(null);

    // Parse and retreive the `state` & `token` JWT from Auth0 via query params
    const location = useLocation();
    const { token, prompt_name, prompt_phone, prompt_email, state } = queryString.parse(location.search);

    const promptForNameInput = prompt_name === 'true';
    const promptForPhoneInput = prompt_phone === 'true';
    const promptForEmailInput = prompt_email === 'true';

    const tenantDomain = window.APP_VARS.REACT_APP_AUTH0_TENANT_DOMAIN;

    const submitUserForm = async (firstName: string, lastName: string, email: string, phoneNumber: number) => {
        setLoadingRequest(true);
        if (token && state) {
            // Update user info
            try {
                const response = await fetch('/api/users', {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                    body: JSON.stringify({
                        firstName,
                        lastName,
                        phoneNumber,
                        userInputtedEmail: email,
                        promptForNameInput,
                        promptForPhoneInput,
                        promptForEmailInput,
                        state,
                        token,
                    }),
                });

                const jsonResponse = await response.json();

                if (!response.ok) {
                    if (response.status === 401) {
                        throw new SessionExpiredException(jsonResponse.message);
                    } else if (response.status === 500) {
                        throw new InternalServiceException(jsonResponse.message);
                    } else {
                        throw new Error(jsonResponse.message);
                    }
                }

                if (response.status === 200) {
                    setLoadingRequest(false);
                    setSuccessfulRequest(true);
                    window.location.replace(
                        `https://${tenantDomain}/continue?state=${state}`,
                    );
                }
            } catch (error) {
                setLoadingRequest(false);
                setErrorRequest(error.message);

                if (error instanceof SessionExpiredException || error instanceof InternalServiceException) {
                    window.location.replace(
                        encodeURI(`${window.APP_VARS.REACT_APP_STOREFRONT_DOMAIN}/login?error=${error.message}`),
                    );
                }
            }
        }
    };

    // If there are no token or state query params, redirect to not found
    if (!token || !state) {
        return <Redirect to="/not-found" />;
    }

    const headerTitle = 'Sign Up';
    const headerDescription = `Let's protect your account. Enter your details for extra security.`;

    return (
        <div className="index-container">
            <div className="index-form-wrapper">
                <Header title={headerTitle} description={headerDescription} />
                <InputForm
                    promptForNameInput={promptForNameInput}
                    promptForPhoneInput={promptForPhoneInput}
                    promptForEmailInput={promptForEmailInput}
                    isRequestLoading={loadingRequest}
                    isRequestSuccessful={successfulRequest}
                    serverErrors={errorRequest}
                    submitUserForm={submitUserForm}
                />
                {loadingRequest && <LoadingSpinner />}
                {!loadingRequest && successfulRequest && <SuccessSpinner />}
            </div>
        </div>
    );
};

export default IndexPage;
